import cn from 'classnames';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Image, Link } from 'components';
import { Row } from 'components/layout';

import { ProfilePics } from 'components/candidate/ProfilePics/ProfilePics.component';
import styles from './TouchpointCard.module.scss';

export interface TouchpointCartDataProps {
  id: string;
  title: string;
  start_date: string;
  url: string;
  avatar_urls: Array<string>;
  applications_count: number;
  event_icon: string;
}

export interface TouchpointCardProps extends TouchpointCartDataProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** title of the opportunity */
}

export const TouchpointCard: React.FC<TouchpointCardProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    title,
    start_date,
    url,
    avatar_urls,
    applications_count,
    event_icon,
  }: TouchpointCardProps) => {
    const contentClassNames = cn(
      {
        [styles.TouchpointCard]: true,
      },
      className
    );

    const [t] = useTranslation('onboarding.manager');

    const maxPicsToShow = applications_count > 10 ? 10 : applications_count;

    const moreApplications =
      applications_count > maxPicsToShow ? (
        <div className={styles.chip}>
          + {applications_count - maxPicsToShow}
        </div>
      ) : (
        <></>
      );

    return (
      <Link
        href={url}
        underlineOnHover={false}
        className={styles.link}
        target="_blank"
      >
        <div
          className={contentClassNames}
          style={style}
          data-testid="TouchpointCard"
        >
          <div className={styles.details}>
            <div>
              <p className={styles.title}>{title}</p>
              <Row className={styles.startDate}>{start_date}</Row>
            </div>
            {event_icon && (
              <div className={styles.eventImage}>
                <Image
                  src={event_icon}
                  alt={t('step-4.touchpoint-card.alt-text', {
                    title,
                  })}
                />
              </div>
            )}
          </div>
          {applications_count ? (
            <>
              <div className={styles.subscribers}>
                <ProfilePics urls={avatar_urls} maxPicsToShow={maxPicsToShow} />
                {moreApplications}
              </div>
              <div className={styles.subscribersCount}>
                {t('step-4.touchpoint-card.applications-stats', {
                  applications_count,
                })}
              </div>
            </>
          ) : null}
        </div>
      </Link>
    );
  }
);

TouchpointCard.displayName = 'TouchpointCard';
